import * as React from 'react'

import { XIcon } from 'lucide-react'
import { observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  Field,
  FieldContent,
  FieldLabel,
} from '~/client/src/shared/components/Field/Field'
import { AppMaterialIcon } from '~/client/src/shared/components/icons/AppMaterialIcon'
import { StateFilledCheckboxCheckboxIcon } from '~/client/src/shared/components/icons/StateFilledCheckboxCheckboxIcon'
import { TerrainIcon } from '~/client/src/shared/components/icons/TerrainIcon'
import { TrafficIcon } from '~/client/src/shared/components/icons/TrafficIcon'
import { IMapSettings } from '~/client/src/shared/components/MapBoxViewer/mapSettings'
import { Kbd } from '~/client/src/shared/components/shadcn-ui/kbd'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IMapSettingsPanelProps {
  settings: IMapSettings
  onClose: () => void
  fullWidth?: boolean
  streetThumbnailSrc?: string
  satelliteThumbnailSrc?: string
}

interface ISectionHeaderProps {
  label: string
}

function SectionHeader({ label }: ISectionHeaderProps) {
  return (
    <div className="tw:flex tw:items-center tw:gap-2.5 tw:px-4 tw:pt-4 tw:pb-2">
      <span className="tw:text-muted-foreground tw:text-label-md-caps-prominent tw:uppercase">
        {label}
      </span>
    </div>
  )
}

interface ISelectorCardProps {
  label: string
  isSelected: boolean
  onClick: () => void
  children: React.ReactNode
}

function SelectorCard({
  label,
  isSelected,
  onClick,
  children,
}: ISelectorCardProps) {
  return (
    <div
      className="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:flex-1 tw:cursor-pointer"
      onClick={onClick}
    >
      <div
        className={`tw:relative tw:flex tw:flex-col tw:rounded-[5px] tw:p-1 tw:w-full tw:h-[52px] tw:border-2 ${
          isSelected ? 'tw:border-primary' : 'tw:border-transparent'
        }`}
      >
        <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:flex-1 tw:rounded tw:bg-muted tw:overflow-hidden">
          {children}
        </div>
        {isSelected && (
          <div className="tw:absolute tw:top-0.5 tw:left-0.5 tw:w-5 tw:h-5 tw:bg-background tw:rounded-sm tw:text-primary">
            <StateFilledCheckboxCheckboxIcon size="sm" />
          </div>
        )}
      </div>
      <span
        className={`tw:text-label-sm tw:text-center tw:w-full ${
          isSelected ? 'tw:text-foreground' : 'tw:text-muted-foreground'
        }`}
      >
        {label}
      </span>
    </div>
  )
}

const STREET_THUMBNAIL =
  '/static/icons/mapbox-icons/map-styles/map-layer-street-wide.png'
const SATELLITE_THUMBNAIL =
  '/static/icons/mapbox-icons/map-styles/map-layer-satellite-wide.png'

const MapSettingsPanel = observer(function MapSettingsPanel(
  props: IMapSettingsPanelProps,
) {
  const {
    settings: {
      mapType,
      isThreeDEnabled,
      isTrafficEnabled,
      isTerrainEnabled,
      isZoomScalingEnabled,
      isLabelOverlapEnabled,
      shouldHideLabels,
      shouldHideWorkflowPinLabels,
      isShowWorkflowMarkupsEnabled,
      isShowWorkflowMarkupLabelsEnabled,
      isWorkflowMarkupsProjectDefault,
      isShowAdjacentMarkupsEnabled,
      onMapTypeChange,
      onToggle3D,
      onToggleTraffic,
      onToggleTerrain,
      onToggleZoomScaling,
      onToggleLabelOverlap,
      onToggleWorkflowPinLabels,
      onToggleLabels,
      onToggleShowWorkflowMarkups,
      onToggleShowWorkflowMarkupLabels,
      onToggleWorkflowMarkupsProjectDefault,
      onToggleShowAdjacentMarkups,
    },
    onClose,
    fullWidth,
    streetThumbnailSrc = STREET_THUMBNAIL,
    satelliteThumbnailSrc = SATELLITE_THUMBNAIL,
  } = props

  return (
    <div
      className={`tw:flex tw:min-h-0 tw:flex-col tw:bg-background tw:text-foreground ${
        fullWidth ? 'tw:w-full tw:rounded-t-lg' : 'tw:w-[400px] tw:rounded-lg'
      }`}
    >
      {/* Header */}
      <div className="tw:flex tw:items-center tw:justify-center tw:relative tw:h-10">
        <span className="tw:text-title-md tw:text-foreground">
          {Localization.translator.mapSettings}
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={Localization.translator.close}
          className="tw:absolute tw:right-2"
          onClick={onClose}
        >
          <XIcon />
        </Button>
      </div>

      {/* The settings scroll under the header when the sheet is shorter than they are. */}
      <div className="tw:min-h-0 tw:overflow-y-auto">
        {/* Map Type */}
        <SectionHeader label={Localization.translator.mapType} />
        <div className="tw:flex tw:gap-2 tw:px-4 tw:pt-1 tw:pb-3">
          <SelectorCard
            label={Localization.translator.street}
            isSelected={mapType === 'street'}
            onClick={mapType === 'satellite' ? onMapTypeChange : undefined}
          >
            <img
              src={streetThumbnailSrc}
              alt="Street"
              className="tw:w-full tw:h-full tw:object-cover"
            />
          </SelectorCard>
          <SelectorCard
            label={Localization.translator.satellite}
            isSelected={mapType === 'satellite'}
            onClick={mapType === 'street' ? onMapTypeChange : undefined}
          >
            <img
              src={satelliteThumbnailSrc}
              alt="Satellite"
              className="tw:w-full tw:h-full tw:object-cover"
            />
          </SelectorCard>
        </div>

        {/* Overlays */}
        <SectionHeader label={Localization.translator.overlays} />
        <div className="tw:flex tw:gap-2 tw:px-4 tw:pt-1 tw:pb-3">
          <SelectorCard
            label="3D"
            isSelected={isThreeDEnabled}
            onClick={onToggle3D}
          >
            <AppMaterialIcon className="tw:text-muted-foreground" size="md" />
          </SelectorCard>
          <SelectorCard
            label={Localization.translator.traffic}
            isSelected={isTrafficEnabled}
            onClick={onToggleTraffic}
          >
            <TrafficIcon className="tw:text-muted-foreground" size="md" />
          </SelectorCard>
          <SelectorCard
            label={Localization.translator.terrain}
            isSelected={isTerrainEnabled}
            onClick={onToggleTerrain}
          >
            <TerrainIcon className="tw:text-muted-foreground" size="md" />
          </SelectorCard>
        </div>

        {/* Labels Display */}
        <SectionHeader label={Localization.translator.labelsDisplay} />
        <div className="tw:flex tw:flex-col tw:pt-1 tw:pb-3">
          <Field orientation="horizontal" className="tw:px-4 tw:py-3">
            <Switch
              checked={!shouldHideLabels}
              onCheckedChange={() => onToggleLabels()}
            />
            <FieldContent>
              <FieldLabel className="tw:text-label-lg tw:text-foreground">
                {Localization.translator.useMapLabels}
              </FieldLabel>
            </FieldContent>
          </Field>
          {onToggleZoomScaling && (
            <Field orientation="horizontal" className="tw:px-4 tw:py-3">
              <Switch
                checked={isZoomScalingEnabled}
                onCheckedChange={() => onToggleZoomScaling()}
              />
              <FieldContent>
                <FieldLabel className="tw:text-label-lg tw:text-foreground">
                  {Localization.translator.allowTextAndIconToScale}
                </FieldLabel>
              </FieldContent>
            </Field>
          )}
          {onToggleLabelOverlap && (
            <Field orientation="horizontal" className="tw:px-4 tw:py-3">
              <Switch
                checked={isLabelOverlapEnabled}
                onCheckedChange={() => onToggleLabelOverlap()}
              />
              <FieldContent>
                <FieldLabel className="tw:text-label-lg tw:text-foreground">
                  {Localization.translator.allowLabelsToOverlap}
                </FieldLabel>
              </FieldContent>
            </Field>
          )}
          {onToggleWorkflowPinLabels && (
            <Field orientation="horizontal" className="tw:px-4 tw:py-3">
              <Switch
                checked={!shouldHideWorkflowPinLabels}
                onCheckedChange={() => onToggleWorkflowPinLabels()}
              />
              <FieldContent>
                <FieldLabel className="tw:text-label-lg tw:text-foreground">
                  {Localization.translator.showWorkflowPinLabels}
                </FieldLabel>
              </FieldContent>
            </Field>
          )}
        </div>

        {onToggleShowWorkflowMarkups && (
          <>
            <SectionHeader label={Localization.translator.workflowMarkups} />
            <div className="tw:flex tw:flex-col tw:pt-1 tw:pb-3">
              <Field orientation="horizontal" className="tw:px-4 tw:py-3">
                <Switch
                  checked={isShowWorkflowMarkupsEnabled}
                  onCheckedChange={() => onToggleShowWorkflowMarkups()}
                />
                <FieldContent>
                  <FieldLabel className="tw:text-label-lg tw:text-foreground">
                    {Localization.translator.showForMe}
                  </FieldLabel>
                  {/* Keyboard shortcut hint — desktop only (no key on mobile). */}
                  {!fullWidth && (
                    <span className="tw:flex tw:items-center tw:gap-1 tw:text-label-sm tw:text-muted-foreground">
                      <Kbd>A</Kbd>
                      {Localization.translator.markupsShortcutHint}
                    </span>
                  )}
                </FieldContent>
                {isShowWorkflowMarkupsEnabled &&
                  onToggleShowWorkflowMarkupLabels && (
                    <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2">
                      <Switch
                        checked={isShowWorkflowMarkupLabelsEnabled}
                        onCheckedChange={() =>
                          onToggleShowWorkflowMarkupLabels()
                        }
                      />
                      <span className="tw:text-label-lg tw:text-foreground">
                        {Localization.translator.showAssociationLabels}
                      </span>
                    </div>
                  )}
              </Field>
              {onToggleWorkflowMarkupsProjectDefault && (
                <Field orientation="horizontal" className="tw:px-4 tw:py-3">
                  <Switch
                    checked={isWorkflowMarkupsProjectDefault}
                    onCheckedChange={() =>
                      onToggleWorkflowMarkupsProjectDefault()
                    }
                  />
                  <FieldContent>
                    <FieldLabel className="tw:text-label-lg tw:text-foreground">
                      {Localization.translator.showWorkflowMarkupsForEveryone}
                    </FieldLabel>
                  </FieldContent>
                </Field>
              )}
            </div>
          </>
        )}

        {onToggleShowAdjacentMarkups && (
          <>
            <SectionHeader label={Localization.translator.workflowMarkups} />
            <div className="tw:flex tw:flex-col tw:pt-1 tw:pb-3">
              <Field orientation="horizontal" className="tw:px-4 tw:py-3">
                <Switch
                  checked={isShowAdjacentMarkupsEnabled}
                  onCheckedChange={() => onToggleShowAdjacentMarkups()}
                />
                <FieldContent>
                  <FieldLabel className="tw:text-label-lg tw:text-foreground">
                    {Localization.translator.showAdjacent}
                  </FieldLabel>
                  {/* Keyboard shortcut hint — desktop only (no key on mobile). */}
                  {!fullWidth && (
                    <span className="tw:flex tw:items-center tw:gap-1 tw:text-label-sm tw:text-muted-foreground">
                      <Kbd>A</Kbd>
                      {Localization.translator.markupsShortcutHint}
                    </span>
                  )}
                </FieldContent>
              </Field>
            </div>
          </>
        )}
      </div>
    </div>
  )
})

export default MapSettingsPanel
